import { useEffect } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { camelCase } from 'case-anything'
import { FieldInputControlled } from 'components/Field/components/FieldInputControlled'
import { Form } from 'components/Form'
import { FormActions } from 'components/FormActions'
import { getCreateRequest } from 'features/dictionaries/components/DictionariesCreatePage/helpers'
import { DictionaryType } from 'features/dictionaries/types'
import { useGoBack } from 'hooks/useGoBack'
import { useHandleError } from 'hooks/useHandleError'
import { useOrganizationID } from 'hooks/useOrganizationID'
import { useToast } from 'hooks/useToast'
import { titleValidationOptions } from 'data'

interface DictionariesCreatePageProps {
  type: DictionaryType
}

export const DictionariesCreatePage = ({ type }: DictionariesCreatePageProps) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useToast()
  const navigate = useNavigate()
  const handleError = useHandleError()
  const organizationID = useOrganizationID()
  const { formState: { isSubmitting, isSubmitted, isSubmitSuccessful }, setFocus, control, getValues, reset, handleSubmit } = useForm()
  const goBack = useGoBack({ getValues })

  const create = async ({ name }: FieldValues) => {
    if (isSubmitting || (isSubmitted && isSubmitSuccessful)) {
      return
    }

    await getCreateRequest(type)(organizationID.toString(), { name })
    enqueueSnackbar(t(`entity.${camelCase(type)}.messages.createSuccess`), { variant: 'success' })

  }

  const handleCreate = async (values: FieldValues) => {
    try {
      await create(values)
      navigate(`/${organizationID}/dictionaries/${type}`)
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  const handleSaveAndContinue = async (values: FieldValues) => {
    try {
      await create(values)
    } catch (error) {
      handleError(error)
      throw error
    }
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
    }
  }, [isSubmitSuccessful, reset, setFocus])

  return (
    <Form
      title={t(`pages.${camelCase(type)}Create`)}
      content={(
        <FieldInputControlled
          name={'name'}
          control={control}
          rules={titleValidationOptions}
          label={t(`entity.${camelCase(type)}.fields.title`)}
          placeholder={t(`entity.${camelCase(type)}.fields.title`)}
        />
      )}
      actions={(
        <FormActions
          isSubmitting={isSubmitting}
          isSubmitSuccessful={isSubmitSuccessful}
          onGoBack={goBack}
          onSubmitAndSave={handleSubmit(handleSaveAndContinue)}
        />
      )}
      onSubmit={handleSubmit(handleCreate)}
    />
  )
}
